import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Droplet, Loader2, List, UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import { clientsAPI, serviceCallsAPI } from '@shared/apiClient';
import { FlagIcon } from '@shared/flagIcons';
import { formatDate, getServiceCallListCustomerName } from '@shared/utils';
import ClientDetailsModal from '../components/ClientDetailsModal';
import AssignClientModal from '../components/AssignClientModal';
import { ServiceCallDetailsModal } from './ServiceCalls';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const MONTH_TAB_LABELS = MONTH_SHORT;

const currentDate = new Date();
const currentMonth = currentDate.getMonth() + 1;
const currentYear = currentDate.getFullYear();
const YEAR_OPTIONS = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

const getRosterDisplayName = (client) => getServiceCallListCustomerName({ client });

const getSortKey = (client) => {
  if (client.isCommercial && client.companyName) {
    return client.companyName.toLowerCase();
  }
  const last = (client.contactLastName || '').toLowerCase();
  const first = (client.contactFirstName || '').toLowerCase();
  return `${last}\t${first}`;
};

const sortRosterRows = (a, b) => {
  const keyA = getSortKey(a.client);
  const keyB = getSortKey(b.client);
  return keyA.localeCompare(keyB);
};

const formatTechName = (tech) => {
  if (!tech) return null;
  return `${tech.firstName || ''} ${tech.lastName || ''}`.trim() || null;
};

const formatLocation = (client) => {
  const parts = [client.city, client.state].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '—';
};

const stopRowClick = (e) => e.stopPropagation();

const ServiceMonthsCell = ({ client }) => {
  const cycleMonths = Array.isArray(client.serviceCycleMonths)
    ? [...client.serviceCycleMonths].sort((a, b) => a - b)
    : [];

  if (cycleMonths.length === 0) {
    return <span className="text-sm text-gray-400">—</span>;
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {cycleMonths.map((cycleMonth) => {
        const isOilChange = client.oilChangeMonth === cycleMonth;
        return (
          <span
            key={cycleMonth}
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
              isOilChange
                ? 'bg-amber-100 text-amber-800 border-amber-300'
                : 'bg-gray-100 text-gray-700 border-transparent'
            }`}
            title={isOilChange ? 'Service (oil change)' : 'Inspection'}
          >
            {isOilChange && <Droplet className="h-3 w-3 mr-0.5 flex-shrink-0" />}
            {MONTH_SHORT[cycleMonth - 1]}
            <span className="ml-0.5 text-[10px] opacity-80">
              {isOilChange ? 'S' : 'I'}
            </span>
          </span>
        );
      })}
    </div>
  );
};

export default function MonthlyRoster({ embedded = false }) {
  const queryClient = useQueryClient();
  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [clientToAssign, setClientToAssign] = useState(null);
  const [selectedServiceCall, setSelectedServiceCall] = useState(null);
  const [isServiceCallModalOpen, setIsServiceCallModalOpen] = useState(false);
  const [loadingServiceCallId, setLoadingServiceCallId] = useState(null);

  const { data, isLoading, error } = useQuery(
    ['monthlyRoster', month, year],
    () => clientsAPI.getServiceHistoryOverview({ month, year }),
    { keepPreviousData: true }
  );

  const clients = data?.data?.clients ?? [];
  const sortedClients = useMemo(
    () => [...clients].sort(sortRosterRows),
    [clients]
  );
  const servicedCount = clients.filter((row) => row.serviced).length;
  const incompleteCount = clients.length - servicedCount;
  const monthLabel = MONTH_NAMES[month - 1];

  const openClientDetails = (client) => {
    setSelectedClient(client);
    setIsDetailsModalOpen(true);
  };

  const openServiceCall = async (serviceCallId) => {
    if (!serviceCallId) return;
    setLoadingServiceCallId(serviceCallId);
    try {
      const response = await serviceCallsAPI.getServiceCall(serviceCallId);
      setSelectedServiceCall(response.data.serviceCall);
      setIsServiceCallModalOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load service call');
    } finally {
      setLoadingServiceCallId(null);
    }
  };

  const handleAssignClose = () => {
    queryClient.invalidateQueries(['monthlyRoster', month, year]);
    setClientToAssign(null);
  };

  const canAssign = (row) =>
    !row.serviced && row.resolution !== 'skipped' && row.resolution !== 'bumped';

  const getRowStatusClass = (row) => {
    if (row.serviced) {
      return 'bg-green-50 hover:bg-green-100/80';
    }
    if (row.resolution === 'skipped') {
      return 'bg-amber-50 hover:bg-amber-100/80';
    }
    if (row.resolution === 'bumped') {
      return 'bg-orange-50 hover:bg-orange-100/80';
    }
    if (row.assignedTo) {
      return 'bg-blue-50 hover:bg-blue-100/80';
    }
    return 'bg-white hover:bg-gray-50';
  };

  const renderAssignedCell = (row) => {
    if (row.serviced) return <span className="text-sm text-gray-400">—</span>;
    if (row.resolution === 'skipped') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
          Skipped
        </span>
      );
    }
    if (row.resolution === 'bumped') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          Bumped
        </span>
      );
    }
    const assignedName = formatTechName(row.assignedTo);
    if (assignedName) {
      return <span className="text-sm text-gray-900">{assignedName}</span>;
    }
    return <span className="text-sm text-gray-400">—</span>;
  };

  return (
    <div className={embedded ? 'px-4 sm:px-6 lg:px-8 py-4' : 'px-4 sm:px-6 lg:px-8 py-6'}>
      {!embedded && (
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <List className="h-7 w-7 text-primary-600" />
          Monthly Roster
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          All regular service customers due in {monthLabel} {year}, A–Z. Row colors: green = serviced;
          blue = assigned; amber = skipped; orange = bumped; white = not started. Service months:{' '}
          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-medium">
            <Droplet className="h-3 w-3 mr-0.5" />
            S
          </span>{' '}
          = oil change, gray <span className="font-medium">I</span> = inspection.
        </p>
      </div>
      )}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <label htmlFor="monthly-roster-year" className="text-sm font-medium text-gray-700">
            Year
          </label>
          <select
            id="monthly-roster-year"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            className="rounded-md border border-gray-300 shadow-sm px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            {YEAR_OPTIONS.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        {!isLoading && clients.length > 0 && (
          <p className="text-sm text-gray-600">
            {servicedCount} of {clients.length} serviced
            {incompleteCount > 0 && `, ${incompleteCount} not complete`}
          </p>
        )}
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <nav
          className="flex flex-wrap border-b border-gray-200 px-2 pt-2"
          aria-label="Service months"
        >
          {MONTH_TAB_LABELS.map((label, i) => {
            const monthValue = i + 1;
            const isActive = month === monthValue;
            return (
              <button
                key={label}
                type="button"
                onClick={() => setMonth(monthValue)}
                title={MONTH_NAMES[i]}
                className={`px-3 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  isActive
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {label}
              </button>
            );
          })}
        </nav>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : error ? (
          <div className="px-6 py-8 text-center text-red-600">
            {error.response?.data?.message || error.message || 'Failed to load monthly roster'}
          </div>
        ) : clients.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            No contract clients are due for service in {monthLabel} {year}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service months
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Completed by
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Flags
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedClients.map((row) => {
                  const c = row.client;
                  const displayName = getRosterDisplayName(c);
                  const completedByName = formatTechName(row.completedBy);
                  const isLoadingCall = loadingServiceCallId === row.serviceCallId;

                  return (
                    <tr
                      key={c.id}
                      className={`transition-colors ${getRowStatusClass(row)}`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => openClientDetails(c)}
                          className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline text-left"
                        >
                          {displayName}
                        </button>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {formatLocation(c)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <ServiceMonthsCell client={c} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {row.invoiceNumber || '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm" onClick={stopRowClick}>
                        {row.serviced && row.completedDate && row.serviceCallId ? (
                          <button
                            type="button"
                            onClick={() => openServiceCall(row.serviceCallId)}
                            disabled={isLoadingCall}
                            className="text-primary-600 hover:text-primary-800 hover:underline disabled:opacity-50"
                          >
                            {isLoadingCall ? 'Loading…' : formatDate(row.completedDate)}
                          </button>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {completedByName || '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {renderAssignedCell(row)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {c.flags && c.flags.length > 0 ? (
                          <div className="flex items-center gap-1.5">
                            {c.flags.map((flag) => (
                              <FlagIcon
                                key={flag.id}
                                iconName={flag.icon}
                                className={`h-4 w-4 ${flag.isActive ? 'text-gray-600' : 'text-gray-400'}`}
                                title={flag.name}
                              />
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right" onClick={stopRowClick}>
                        {canAssign(row) ? (
                          <button
                            type="button"
                            onClick={() => setClientToAssign(c)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            title="Assign to technician"
                          >
                            <UserPlus className="h-4 w-4" />
                            Assign
                          </button>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ClientDetailsModal
        client={selectedClient}
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedClient(null);
        }}
      />

      <AssignClientModal
        client={clientToAssign}
        isOpen={!!clientToAssign}
        onClose={handleAssignClose}
      />

      <ServiceCallDetailsModal
        serviceCall={selectedServiceCall}
        isOpen={isServiceCallModalOpen}
        onClose={() => {
          setIsServiceCallModalOpen(false);
          setSelectedServiceCall(null);
        }}
      />
    </div>
  );
}
